/**
 * Entity handle and builder.
 *
 * Entities are lightweight uint32 handles. Each entity is backed by a Three.js
 * Object3D for scene graph integration.
 */

import type * as THREE from 'three/webgpu';
import type { ComponentDef, ComponentSchema, TagDef } from './Component.js';
import type { World } from './World.js';

// ─── Lifecycle Hooks ────────────────────────────────────────────────────────────

export type LifecycleHook = (entity: number) => void;

export interface EntityLifecycle {
  onInit?: LifecycleHook;
  onEnable?: LifecycleHook;
  onDisable?: LifecycleHook;
  onDestroy?: LifecycleHook;
}

// ─── Entity State ───────────────────────────────────────────────────────────────

export const enum EntityState {
  Created = 0,
  Initialized = 1,
  Active = 2,
  Disabled = 3,
  Destroyed = 4,
}

export interface EntityRecord {
  id: number;
  name: string;
  state: EntityState;
  object3D: THREE.Object3D;
  lifecycle: EntityLifecycle;
  parent: number | null;
}

// ─── Entity Builder ─────────────────────────────────────────────────────────────

export class EntityBuilder {
  private components: Array<{ def: ComponentDef; data?: Record<string, unknown> }> = [];
  private tags: TagDef[] = [];
  private lifecycle: EntityLifecycle = {};
  private _name: string;
  private _parent: number | null = null;

  constructor(
    private world: World,
    name: string,
  ) {
    this._name = name;
  }

  add<S extends ComponentSchema>(
    def: ComponentDef<S>,
    data?: Partial<Record<keyof S, unknown>>,
  ): this {
    this.components.push({ def, data: data as Record<string, unknown> });
    return this;
  }

  tag(def: TagDef): this {
    this.tags.push(def);
    return this;
  }

  onInit(fn: LifecycleHook): this {
    this.lifecycle.onInit = fn;
    return this;
  }

  onEnable(fn: LifecycleHook): this {
    this.lifecycle.onEnable = fn;
    return this;
  }

  onDisable(fn: LifecycleHook): this {
    this.lifecycle.onDisable = fn;
    return this;
  }

  onDestroy(fn: LifecycleHook): this {
    this.lifecycle.onDestroy = fn;
    return this;
  }

  parent(entity: number): this {
    this._parent = entity;
    return this;
  }

  build(): number {
    return this.world._buildEntity(
      this._name,
      this.components,
      this.tags,
      this.lifecycle,
      this._parent,
    );
  }
}
